import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Progress } from "@/components/ui/progress";
import {
  IdCard, Briefcase, Wallet, Star, ShieldCheck, ShieldAlert, MapPin,
  Calendar, Bell, ArrowRight, TrendingUp, AlertTriangle,
} from "lucide-react";

export default function ProfessionalDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/dashboard").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === null) return <Loader label="Loading your workforce dashboard" />;
  if (!data) return <Empty label="Could not load dashboard" />;

  const p = data.profile;
  const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="fade-up">
      <PageHeader overline="Professional workspace" title={`Welcome, ${p.name.split(" ")[0]}`}
        subtitle="Your assignments, opportunities, earnings and credentials at a glance."
        testid="professional-dashboard" />

      {/* Identity strip */}
      <div className="krida-card p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <img src={p.photo} alt={p.name} className="w-20 h-20 rounded-2xl object-cover border border-white/10" />
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-heading text-2xl font-bold">{p.name}</h2>
            <Tag tone={statusTone(p.verification_status)}>
              {p.verification_status === "Verified" ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
              {p.verification_status}
            </Tag>
            <span className="font-mono text-xs text-krida-emerald">{p.krida_id}</span>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
            <span className="flex items-center gap-1"><Star className="w-4 h-4 text-krida-amber" /> {p.rating}</span>
            <span>{p.primary_level}</span>
          </div>
          <div className="mt-3 max-w-md">
            <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Profile completeness</span><span className="font-mono">{p.profile_completeness}%</span></div>
            <Progress value={p.profile_completeness} className="h-2 bg-slate-800" />
          </div>
        </div>
        <Link to="/app/pass" data-testid="view-pass-btn" className="px-4 py-2.5 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold text-sm flex items-center gap-2 transition-colors">
          <IdCard className="w-4 h-4" /> View Krida Pass
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Assignments" value={data.assignment_count} icon={Briefcase} accent="cobalt" testid="stat-assignments" />
        <StatCard label="Total earnings" value={inr(data.earnings.total)} icon={Wallet} accent="emerald" testid="stat-earnings" />
        <StatCard label="Pending" value={inr(data.earnings.pending)} icon={TrendingUp} accent="amber" />
        <StatCard label="Expiring creds" value={data.expiring_credentials.length} icon={AlertTriangle} accent="crimson" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Opportunities */}
          <div className="krida-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Briefcase className="w-5 h-5 text-krida-cobalt" /> Top matched opportunities</h3>
              <Link to="/app/work" className="text-sm text-krida-crimson flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="space-y-3">
              {data.top_opportunities.length === 0 && <Empty label="No matching opportunities" />}
              {data.top_opportunities.map((o) => (
                <div key={o.id} className="flex items-center gap-4 p-3 rounded-xl bg-black/20 border border-white/5" data-testid={`opp-${o.id}`}>
                  <div className="w-14 h-14 rounded-xl grid place-items-center font-mono font-bold text-lg bg-krida-emerald/10 text-krida-emerald border border-krida-emerald/20">
                    {o.match?.score}%
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{o.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      <MapPin className="w-3 h-3" /> {o.location.city} · {o.org_name}
                    </div>
                    <div className="text-xs text-slate-400 mt-1 truncate">{(o.match?.reasons || []).slice(0, 2).join(" · ")}</div>
                  </div>
                  <Tag tone="cobalt">{o.opportunity_type}</Tag>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming assignments */}
          <div className="krida-card p-6">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2 mb-4"><Calendar className="w-5 h-5 text-krida-amber" /> Upcoming assignments</h3>
            <div className="space-y-3">
              {data.upcoming_assignments.length === 0 && <Empty label="No upcoming assignments" />}
              {data.upcoming_assignments.map((a) => (
                <div key={a.id} className="flex items-center justify-between p-3 rounded-xl bg-black/20 border border-white/5" data-testid={`assign-${a.id}`}>
                  <div>
                    <div className="font-semibold">{a.role} · {a.sport}</div>
                    <div className="text-xs text-slate-500">{a.tournament_name} · {a.venue}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{new Date(a.date).toLocaleDateString()} · Report {a.reporting_time}</div>
                  </div>
                  <div className="text-right">
                    <Tag tone={statusTone(a.status)}>{a.status.replace("_", " ")}</Tag>
                    <div className="font-mono text-sm mt-2">₹{a.fee?.toLocaleString("en-IN")}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <div className="krida-card p-6">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-krida-crimson" /> Notifications</h3>
            <div className="space-y-3">
              {data.notifications.map((n) => (
                <div key={n.id} className="text-sm border-l-2 border-krida-crimson/40 pl-3 py-1">
                  <div className="font-semibold">{n.title}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{n.body}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="krida-card p-6">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-krida-amber" /> Credential alerts</h3>
            {data.expiring_credentials.length === 0 && <div className="text-sm text-slate-500">All credentials valid.</div>}
            <div className="space-y-3">
              {data.expiring_credentials.map((c) => (
                <div key={c.id} className="flex items-center justify-between text-sm">
                  <div className="min-w-0"><div className="font-medium truncate">{c.type}</div><div className="text-xs text-slate-500">{c.issuer}</div></div>
                  <Tag tone={c.days_to_expiry < 0 ? "crimson" : "amber"}>{c.days_to_expiry < 0 ? "Expired" : `${c.days_to_expiry}d`}</Tag>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
